import { Link, useLocation } from "react-router-dom";
import { memo, useCallback, useMemo } from "react";
import { useRoutes } from "../navigation/navigationConfig";
import { Dropdown } from "react-bootstrap";

const NavMenu = memo(({ showIcons = true, onRouteClick }) => {
  const location = useLocation();
  const routes = useRoutes();
  
  const filteredRoutes = useMemo(() => 
    routes.filter((item) => item.showIn?.includes("publicHeader")),
    [routes]
  );

  const { primaryRoutes, exploreRoutes, moreRoutes } = useMemo(() => {
    const explore = filteredRoutes.filter((r) => r.headerNavGroup === "explore");
    const primary = filteredRoutes.filter((r) => r.headerNavPrimary === true);
    const usedHrefs = new Set([...primary, ...explore].map((r) => r.href));
    const more = filteredRoutes.filter((r) => !usedHrefs.has(r.href));

    return { primaryRoutes: primary, exploreRoutes: explore, moreRoutes: more };
  }, [filteredRoutes]);

  const isActiveHref = useCallback(
    (href) => {
      if (!href) return false;
      const h = String(href);
      if (h.includes("?")) {
        return `${location.pathname}${location.search}` === h;
      }
      if (location.pathname !== href) {
        return location.pathname.startsWith(`${href}/`);
      }
      return !location.search;
    },
    [location.pathname, location.search]
  );

  const exploreActive = useMemo(
    () => exploreRoutes.some((r) => isActiveHref(r.href)),
    [exploreRoutes, isActiveHref]
  );

  const moreActive = useMemo(
    () => moreRoutes.some((r) => isActiveHref(r.href)),
    [moreRoutes, isActiveHref]
  );

  return (
    <ul
      className="navbar-nav mx-auto mb-2 mb-lg-0 flex-nowrap site-header-nav-menu-scroll"
    >
      {primaryRoutes.map((item) => (
        <li className="nav-item" key={item.href}>
          <Link
            to={item.href}
            className={`nav-link d-flex align-items-center px-3 py-2 ${
              isActiveHref(item.href) ? "active" : ""
            } ${item.danger ? "text-danger" : ""}`}
            onClick={() => onRouteClick(item)}
          >
            {showIcons && item.icon && <i className={`bi ${item.icon} me-1`}></i>}
            {item.label}
          </Link>
        </li>
      ))}

      {exploreRoutes.length > 0 && (
        <li className="nav-item">
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              type="button"
              className={`nav-link d-flex align-items-center px-3 py-2 btn btn-link ${
                exploreActive ? "active" : ""
              }`}
              id="public-header-explore-dropdown"
            >
              {showIcons && <i className="bi bi-grid me-1" />}
              Explore
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-dark shadow site-header-dropdown-menu">
              {exploreRoutes.map((item) => (
                <Dropdown.Item
                  as={Link}
                  to={item.href}
                  key={item.href}
                  onClick={() => onRouteClick(item)}
                  active={isActiveHref(item.href)}
                >
                  {showIcons && item.icon && <i className={`bi ${item.icon} me-2`} />}
                  {item.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </li>
      )}

      {moreRoutes.length > 0 && (
        <li className="nav-item">
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              type="button"
              className={`nav-link d-flex align-items-center px-3 py-2 btn btn-link ${
                moreActive ? "active" : ""
              }`}
              id="topbar-more-dropdown"
            >
              {showIcons && <i className="bi bi-three-dots me-1" />}
              More
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-dark shadow topbar-dropdown-menu">
              {moreRoutes.map((item) => (
                <Dropdown.Item
                  as={Link}
                  to={item.href}
                  key={item.href}
                  onClick={() => onRouteClick(item)}
                  active={isActiveHref(item.href)}
                  className={item.danger ? "text-danger" : undefined}
                >
                  {showIcons && item.icon && <i className={`bi ${item.icon} me-2`} />}
                  {item.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </li>
      )}
    </ul>
  );
});

NavMenu.displayName = 'NavMenu';

export default NavMenu;
